import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Mail, Phone, MapPin, Clock, ArrowRight } from "lucide-react";
import { Logo } from "./Logo";
import { SHOP_LINKS } from "@/lib/data";
import { useSettings } from "@/lib/settings";
import { api } from "@/lib/api";
import { toast } from "sonner";

const QUICK = [
  { label: "Shop All", to: "/shop" },
  { label: "Services", to: "/services" },
  { label: "Gallery", to: "/gallery" },
  { label: "About Us", to: "/about" },
  { label: "Contact Us", to: "/contact" },
  { label: "Get a Quote", to: "/quote" },
];

export const Footer = () => {
  const { settings } = useSettings();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter", { email });
      toast.success(data.status === "already_subscribed" ? "You're already on the list!" : "You're in the loop! 🎉");
      setEmail("");
    } catch {
      toast.error("Please enter a valid email.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-ink text-[#f2f0ea]" data-testid="footer">
      <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-12">
        <div className="lg:col-span-2">
          <h4 className="font-display text-lg tracking-[0.12em] mb-3">STAY IN THE LOOP</h4>
          <p className="text-sm text-white/60 mb-5 leading-relaxed">New products, special offers and printing tips.</p>
          <form onSubmit={subscribe} className="flex border border-white/25 max-w-[340px]" data-testid="newsletter-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              data-testid="newsletter-input"
              className="bg-transparent px-4 py-3 text-sm w-full min-w-0 outline-none placeholder:text-white/40"
            />
            <button type="submit" disabled={loading} data-testid="newsletter-submit" className="bg-gold text-ink px-4 hover:bg-gold-soft transition-colors">
              <ArrowRight size={18} />
            </button>
          </form>
        </div>

        <div className="flex flex-col items-start md:items-center text-left md:text-center">
          <Logo light size="lg" />
          <p className="text-sm text-white/55 mt-4 leading-relaxed max-w-[240px]">
            Custom apparel & printing in New Jersey for businesses, schools, teams, events and more. Quality you can feel.
          </p>
          <div className="flex gap-4 mt-5">
            {[
              { Icon: Instagram, href: `https://www.instagram.com/${settings.instagram}`, label: "Instagram" },
              { Icon: Facebook, href: "#", label: "Facebook" },
              { Icon: Mail, href: `mailto:${settings.email}`, label: "Email" },
            ].map(({ Icon, href, label }) => (
              <a key={label} href={href} target="_blank" rel="noreferrer" aria-label={label} data-testid={`social-${label.toLowerCase()}`} className="w-9 h-9 border border-white/25 flex items-center justify-center hover:bg-gold hover:text-ink hover:border-gold transition-all">
                <Icon size={16} />
              </a>
            ))}
          </div>
        </div>

        <div>
          <h4 className="font-display text-sm tracking-[0.16em] mb-5">QUICK LINKS</h4>
          <ul className="space-y-3">
            {QUICK.map((q) => (
              <li key={q.label}>
                <Link to={q.to} className="text-sm text-white/60 hover:text-gold transition-colors">{q.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-display text-sm tracking-[0.16em] mb-5">SHOP</h4>
          <ul className="space-y-3">
            {SHOP_LINKS.map((s) => (
              <li key={s}><Link to="/shop" className="text-sm text-white/60 hover:text-gold transition-colors">{s}</Link></li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-display text-sm tracking-[0.16em] mb-5">CONTACT US</h4>
          <ul className="space-y-4 text-sm text-white/60">
            <li><a href={`tel:${settings.phone.replace(/[^0-9+]/g, "")}`} data-testid="footer-phone-link" className="flex items-center gap-3 hover:text-gold transition-colors"><Phone size={15} className="text-gold" /> {settings.phone}</a></li>
            <li><a href={`mailto:${settings.email}`} data-testid="footer-email-link" className="flex items-center gap-3 hover:text-gold transition-colors"><Mail size={15} className="text-gold" /> {settings.email}</a></li>
            <li><a href={`https://maps.google.com/?q=${encodeURIComponent(settings.location)}`} target="_blank" rel="noreferrer" data-testid="footer-map-link" className="flex items-center gap-3 hover:text-gold transition-colors"><MapPin size={15} className="text-gold" /> {settings.location}</a></li>
            <li className="flex items-center gap-3"><Clock size={15} className="text-gold" /> {settings.hours}</li>
            <li>
              <a href={`https://www.instagram.com/${settings.instagram}`} target="_blank" rel="noreferrer" data-testid="footer-instagram" className="flex items-center gap-3 hover:text-gold transition-colors">
                <Instagram size={15} className="text-gold" /> @{settings.instagram}
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-6 text-center text-xs text-white/45 tracking-wide">
        © 2024 Briars Unlimited. All Rights Reserved.
      </div>
    </footer>
  );
};
